import { Component, ChangeDetectionStrategy, inject, signal, OnInit, OnDestroy } from '@angular/core';
import { IonIcon } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { cloudOfflineOutline, cloudDoneOutline } from 'ionicons/icons';
import { Subscription, filter } from 'rxjs';
import { NetworkService } from '../../core/services/network.service';

/**
 * Offline/Online banner displayed below the header.
 *
 * - When offline: shows "Sin conexión — tus datos se guardan localmente"
 * - When connectivity restored: briefly shows "✓ Conexión restaurada" (3s)
 *
 * Height: 40px. Respects prefers-reduced-motion (instant transition via global CSS).
 *
 * Requirements: 1.4, 9.1
 */
@Component({
  selector: 'fsm-offline-banner',
  standalone: true,
  imports: [IonIcon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (!networkService.isOnline()) {
      <div class="fsm-offline-banner" data-testid="offline-banner" role="status" aria-live="polite">
        <ion-icon name="cloud-offline-outline" aria-hidden="true"></ion-icon>
        Sin conexión — tus datos se guardan localmente
      </div>
    } @else if (showRestored()) {
      <div class="fsm-online-banner" data-testid="online-banner" role="status" aria-live="polite">
        <ion-icon name="cloud-done-outline" aria-hidden="true"></ion-icon>
        ✓ Conexión restaurada — sincronizando...
      </div>
    }
  `,
  styles: [`
    .fsm-online-banner {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 8px 16px;
      background: var(--fsm-success-50, #f0fdf4);
      color: var(--fsm-success-700, #15803d);
      font-size: 13px;
      font-weight: 500;
      animation: fadeInOut 3s ease-in-out;
    }
    @keyframes fadeInOut {
      0% { opacity: 0; }
      10% { opacity: 1; }
      80% { opacity: 1; }
      100% { opacity: 0; }
    }
  `],
})
export class OfflineBannerComponent implements OnInit, OnDestroy {
  protected readonly networkService = inject(NetworkService);
  readonly showRestored = signal(false);
  private sub: Subscription | null = null;
  private hideTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    addIcons({ cloudOfflineOutline, cloudDoneOutline });
  }

  ngOnInit(): void {
    // Show "restored" banner briefly when going from offline → online
    this.sub = this.networkService.onStatusChange
      .pipe(filter((online) => online))
      .subscribe(() => {
        this.showRestored.set(true);
        if (this.hideTimeout) clearTimeout(this.hideTimeout);
        this.hideTimeout = setTimeout(() => this.showRestored.set(false), 3000);
      });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    if (this.hideTimeout) clearTimeout(this.hideTimeout);
  }
}
